import type { GameMode, HighScore } from "../types";

const KEYS = {
  scores: "3dtanks.scores.v2",
  legacyScores: "3dtanks.scores",
} as const;

interface LegacyHighScore {
  score: number;
  date: string;
}

export class LocalStore {
  getHighScores(mode?: GameMode): HighScore[] {
    const scores = this.readScores();
    return mode ? scores.filter((s) => s.mode === mode).slice(0, 10) : scores.slice(0, 10);
  }

  addHighScore(
    score: number,
    options: { mode?: GameMode; waveReached?: number; levelReached?: string } = {},
  ): HighScore[] {
    const mode = options.mode ?? "campaign";
    const scores = this.readScores();
    const entry: HighScore = {
      score,
      date: new Date().toISOString(),
      mode,
      ...(options.waveReached === undefined ? {} : { waveReached: options.waveReached }),
      ...(options.levelReached === undefined ? {} : { levelReached: options.levelReached }),
    };
    scores.push(entry);
    const next = trimByMode(scores);
    localStorage.setItem(KEYS.scores, JSON.stringify(next));
    return next.filter((s) => s.mode === mode);
  }

  private readScores(): HighScore[] {
    try {
      const raw = localStorage.getItem(KEYS.scores);
      if (raw) return normalizeScores(JSON.parse(raw) as unknown);

      const legacyRaw = localStorage.getItem(KEYS.legacyScores);
      if (!legacyRaw) return [];
      const migrated = normalizeLegacyScores(JSON.parse(legacyRaw) as unknown);
      localStorage.setItem(KEYS.scores, JSON.stringify(migrated));
      return migrated;
    } catch {
      return [];
    }
  }
}

function normalizeScores(value: unknown): HighScore[] {
  if (!Array.isArray(value)) return [];
  return trimByMode(
    value.flatMap((item): HighScore[] => {
      if (!isRecord(item) || typeof item.score !== "number" || typeof item.date !== "string") {
        return [];
      }
      const mode = item.mode === "survival" ? "survival" : "campaign";
      return [
        {
          score: item.score,
          date: item.date,
          mode,
          ...(typeof item.waveReached === "number" ? { waveReached: item.waveReached } : {}),
          ...(typeof item.levelReached === "string" ? { levelReached: item.levelReached } : {}),
        },
      ];
    }),
  );
}

function normalizeLegacyScores(value: unknown): HighScore[] {
  if (!Array.isArray(value)) return [];
  const legacy = value.filter(isLegacyHighScore);
  return trimByMode(legacy.map((s) => ({ ...s, mode: "campaign" as const })));
}

function trimByMode(scores: HighScore[]): HighScore[] {
  const byMode: HighScore[] = [];
  for (const mode of ["campaign", "survival"] as const) {
    byMode.push(
      ...scores
        .filter((s) => s.mode === mode)
        .sort((a, b) => b.score - a.score)
        .slice(0, 10),
    );
  }
  return byMode;
}

function isLegacyHighScore(value: unknown): value is LegacyHighScore {
  return isRecord(value) && typeof value.score === "number" && typeof value.date === "string";
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}
